import { useState } from "react";

import { AddressWriteMutation, IAddressFormValues, IVerifyAddress } from "common/types/addresses";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Alert from "client/common/components/content/Alert";
import SelectPanel from "client/common/components/inputs/SelectPanel";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import styles from "../../styles/NewAddressConfirmationDrawer.module.less";

type AddressProps = {
  address: IAddressFormValues;
};

const Address = ({ address: { address_one, address_two, city, state, zipcode } }: AddressProps) => (
  <div className={styles.address}>
    <div>
      {address_one} {address_two}
    </div>
    <div>
      {city}, {state}, {zipcode}
    </div>
  </div>
);

export type NewAddressConfirmationDrawerProps = {
  data: IVerifyAddress;
  onConfirm: (body: AddressWriteMutation) => void;
};

const NewAddressConfirmationDrawer = ({ data, onConfirm }: NewAddressConfirmationDrawerProps) => {
  const { close } = useCurrentDrawer();
  const {
    address_fields: cleanedAddress,
    user_provided_address: userAddress,
    is_deliverable: isDeliverable,
  } = data;
  const [useCleaned, setUseCleaned] = useState(!!cleanedAddress);
  const [isLoading, setIsLoading] = useState(false);

  const onConfirmAddress = () => {
    setIsLoading(true);
    const body = {
      ...data.user_provided_address,
      cache_key: data.cache_key,
      use_cached_address: useCleaned,
    };
    onConfirm(body);
    close();
  };

  const cleanedOption = {
    label: "Suggested address",
    address: cleanedAddress,
    value: true,
  };

  const userOption = {
    label: "Inputted address",
    address: userAddress,
    value: false,
  };

  const options = [cleanedOption, userOption];

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Did you mean..."
        description="Review the address to make sure it's correct"
        onClose={close}
      />
      <DrawerLayout.Form
        submitText="Save address"
        isLoading={isLoading}
        onSubmit={onConfirmAddress}
        canSubmit={true}
      >
        {!isDeliverable && (
          <DrawerLayout.Alerts>
            <Alert variant="warning" actions={[{ label: "Edit", onClick: close }]}>
              {data.message}
            </Alert>
          </DrawerLayout.Alerts>
        )}
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Select an address</DrawerLayout.Form.Label>
          <SelectPanel
            entityName="Address"
            getKey={(address) => `${address?.value}`}
            options={options}
            renderOption={(option) => (
              <div className={styles.option}>
                <div className={styles.label}>{option.label}</div>
                <Address address={option.address} />
              </div>
            )}
            value={useCleaned ? cleanedOption : userOption}
            onClick={(option) => setUseCleaned(option.value)}
          />
        </DrawerLayout.Form.Group>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default NewAddressConfirmationDrawer;
